import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import { useBreadcrumb } from '../layouts/BreadcrumbContext';
import LaboratoryProducts from '../components/Laboratory/LaboratoryProducts';
import LaboratoryLogo from '../components/Laboratory/LaboratoryLogo';
import InlineLoader from '../components/Loader/InlineLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCalendarAlt, faPhone, faTag } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../api';

const getTypeLabels = (t) => ({
    LABORATOIRE: t('Laboratory'),
    REPARTITEUR: t('laboratory_details.type.repartiteur'),
});

const TYPE_STYLES = {
    LABORATOIRE: { background: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
    REPARTITEUR: { background: '#faf5ff', color: '#7c3aed', border: '#e9d5ff' },
};

const INFO_BADGE_STYLE = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: 12, fontWeight: 500, color: '#374151',
    background: '#f9fafb', border: '1px solid #e5e7eb',
    borderRadius: 20, padding: '4px 10px', whiteSpace: 'nowrap',
};

export default function LaboratoryDetails() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [lab, setLab] = useState(null);
    const [loading, setLoading] = useState(true);
    const { setTitle, setTitleAvatar, setTitleDescription } = usePageTitle();
    const { setBreadcrumb } = useBreadcrumb();
    const TYPE_LABELS = getTypeLabels(t);

    useEffect(() => {
        axios
            .get(`${API_BASE_URL}/api/v1/laboratories/${id}`, { withCredentials: true })
            .then(({ data }) => {
                setLab(data);
                const addr = data.addresses?.[0];
                const fullAddress = addr && [
                    [addr.streetNumber, addr.street].filter(Boolean).join(' '),
                    [addr.postalCode, addr.city].filter(Boolean).join(' '),
                ].filter(Boolean).join(', ');
                setTitle(data.name);
                setTitleDescription(fullAddress || null);
                document.title = data.name;
                setTitleAvatar(<LaboratoryLogo id={data.id} name={data.name} size={36} />);
                setBreadcrumb([
                    { label: t('Suppliers'), url: '/suppliers' },
                    { label: data.name },
                ]);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
        return () => { setTitleAvatar(null); setTitleDescription(null); };
    }, [id, t]);

    if (loading) return <InlineLoader loadingMessage={t('Loading...')} />;
    if (!lab) return <div className="page-content text-muted p-4">{t('laboratory_details.not_found')}</div>;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Lab info — compact bar */}
            <div style={{
                flexShrink: 0,
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12,
                padding: '12px 20px',
                borderBottom: '1px solid #f0f1f3',
                background: '#fff',
            }}>
                <LaboratoryLogo id={lab.id} name={lab.name} size={32} />
                <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{lab.name}</span>

                {lab.type && (() => {
                    const s = TYPE_STYLES[lab.type] ?? TYPE_STYLES.LABORATOIRE;
                    return (
                        <span style={{
                            fontSize: 11, fontWeight: 600,
                            background: s.background, color: s.color,
                            border: `1px solid ${s.border}`,
                            borderRadius: 20, padding: '2px 10px',
                        }}>
                            {TYPE_LABELS[lab.type] ?? lab.type}
                        </span>
                    );
                })()}

                {lab.email && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#6b7280' }}>
                        <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 11 }} />
                        {lab.email}
                    </span>
                )}
                {lab.creationDate && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#6b7280' }}>
                        <FontAwesomeIcon icon={faCalendarAlt} style={{ fontSize: 11 }} />
                        {lab.creationDate}
                    </span>
                )}

                {lab.telephone && (
                    <span style={{ ...INFO_BADGE_STYLE, marginLeft: 'auto' }}>
                        <FontAwesomeIcon icon={faPhone} style={{ fontSize: 11 }} />
                        {lab.telephone}
                    </span>
                )}
            </div>

            {/* Products table — fills remaining height so infinite scroll works */}
            <div style={{ flex: 1, minHeight: 0 }}>
                <LaboratoryProducts laboratoryId={id} />
            </div>
        </div>
    );
}
